import { useState, useEffect, useMemo } from 'react';
import {
  collection, onSnapshot, doc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { useItemParam } from '../hooks/useNewParam';
import { Button, Modal, ModalBanner, ConfirmModal, FormField, DeleteIconButton, EntityCombobox } from '../components/ui';
import { HeaderSearch, ChoiceButton, DateRangeButton, StatChips, IconPlusSm } from '../components/HeaderControls';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import {
  buildEntry, postEntry, entryId,
  linesStockLoss, linesStockGain, linesStockWriteoff,
} from '../utils/ledger';
import type { InventoryItem, StockAdjustment, StockAdjustKind } from '../types';

// ── Helpers (per-page, matching convention) ──────────────────────────────
function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
// createdAt is written via serverTimestamp() (a Firestore Timestamp once
// synced back) even though the app's own types declare it as `string` — a
// plain .localeCompare() on it throws and crashes the page the moment two
// rows share the same date and the sort's tie-breaker actually runs. Reads
// either shape (Timestamp instance, plain {seconds}, or an ISO string)
// into sortable millis instead. Mirrors Dashboard.tsx's own toMillis.
function toMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') return new Date(v).getTime();
  const obj = v as { toMillis?: () => number; seconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') return obj.seconds * 1000;
  return 0;
}
const fmtDate = (s?: string) =>
  s ? new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const availCarat = (i: InventoryItem) => round2((i.carat ?? 0) - (i.memoCommittedCarat ?? 0));

const PAGE_SIZE = 50;

// ── Pagination (same pattern as Purchase/Sales/Inventory/Entity/Memos/AP/AR/SalesReturns) ──
function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3)         pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> adjustment{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

const KIND_LABEL: Record<StockAdjustKind, string> = { loss: 'Loss / Shrinkage', gain: 'Found / Gain', writeoff: 'Write-off' };
const KIND_BLURB: Record<StockAdjustKind, string> = {
  loss:     'Some carat / value is gone (miscount, breakage, shrinkage). Reduces stock value.',
  gain:     'Extra carat / value turned up (recount, previously missed). Increases stock value.',
  writeoff: 'The whole item is a total loss (lost, stolen, damaged beyond value). Removes it from stock.',
};

// ── Add modal ───────────────────────────────────────────────────────────
function AdjustModal({ items, onClose, initialItemId }: { items: InventoryItem[]; onClose: () => void; initialItemId?: string }) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [itemId, setItemId] = useState(initialItemId ?? '');
  const [kind, setKind] = useState<StockAdjustKind>('loss');
  const [date, setDate] = useState(todayISO());
  const [caratMag, setCaratMag] = useState('');
  const [costMag, setCostMag] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const item = items.find(i => i.id === itemId) ?? null;
  const isWriteoff = kind === 'writeoff';

  async function save() {
    if (!item) return setError('Pick an item.');
    if (!reason.trim()) return setError('Enter a reason.');
    const cMag = isWriteoff ? availCarat(item) : (parseFloat(caratMag) || 0);
    const $Mag = isWriteoff ? round2(item.costUSD ?? 0) : (parseFloat(costMag) || 0);
    if (!isWriteoff && cMag <= 0 && $Mag <= 0) return setError('Enter a carat and/or value change.');
    if (kind === 'loss' && cMag > availCarat(item) + 1e-6) return setError(`Only ${availCarat(item).toFixed(2)} ct is available to remove.`);
    if (kind === 'loss' && $Mag > (item.costUSD ?? 0) + 1e-6) return setError(`Value removed can't exceed the item's ${money(item.costUSD ?? 0)} cost.`);

    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }

    const sign = kind === 'gain' ? 1 : -1;
    const caratDelta = round2(sign * cMag);
    const costDelta = round2(sign * $Mag);
    // Keep the management "costing" figure in step with the actual cost move.
    const costRatio = (item.costUSD ?? 0) > 0 ? $Mag / (item.costUSD as number) : 0;
    const costingMag = isWriteoff ? round2(item.costingUSD ?? 0) : round2((item.costingUSD ?? 0) * costRatio);
    const costingDelta = round2(sign * costingMag);

    const prefix = (isWriteoff ? settings.stockWriteoffPrefix || 'WO' : settings.stockAdjustmentPrefix || 'AD').toUpperCase();
    const adjRef = doc(collection(db, 'stockAdjustments'));
    const itemRef = doc(db, 'inventory', item.id);
    const counterRef = doc(db, 'counters', business.id);
    const counterField = isWriteoff ? 'stockWriteoff' : 'stockAdjustment';

    try {
      await runTransaction(db, async tx => {
        const [cs, is] = await Promise.all([tx.get(counterRef), tx.get(itemRef)]);
        if (!is.exists()) throw new Error('That item no longer exists.');
        const cur = is.data() as InventoryItem;
        if (cur.status !== 'in-stock') throw new Error('That item is no longer in stock.');

        const n = (cs.exists() ? (cs.data()[counterField] ?? 0) : 0) + 1;
        const adjNo = `${prefix}-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`;

        const newCarat = round2((cur.carat ?? 0) + caratDelta);
        const gone = isWriteoff || newCarat <= 1e-6;

        tx.set(counterRef, { [counterField]: n }, { merge: true });

        tx.update(itemRef, stripUndef(gone
          ? {
              status: 'written-off',
              writtenOffDate: date,
              writtenOffReason: reason.trim(),
              updatedAt: serverTimestamp(),
            }
          : {
              carat: newCarat,
              costUSD: round2((cur.costUSD ?? 0) + costDelta),
              costingUSD: round2((cur.costingUSD ?? 0) + costingDelta),
              updatedAt: serverTimestamp(),
            }));

        tx.set(adjRef, stripUndef({
          businessId: business.id, adjustmentNo: adjNo, kind,
          inventoryItemId: item.id, code: cur.code || undefined, shape: cur.shape,
          date, caratDelta, costDelta, costingDelta,
          reason: reason.trim(), notes: notes.trim() || undefined,
          createdAt: new Date().toISOString(), createdBy: user!.id,
        }));

        const sType = isWriteoff ? 'stock_writeoff' : 'stock_adjustment';
        const lines = kind === 'gain'
          ? linesStockGain(Math.abs(costDelta))
          : isWriteoff
            ? linesStockWriteoff(Math.abs(costDelta))
            : linesStockLoss(Math.abs(costDelta));
        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: sType, sourceId: adjRef.id,
          sourceCollection: 'stockAdjustments', date, currency: settings.currency,
          createdBy: user!.id, note: `${KIND_LABEL[kind]} — ${reason.trim()}`,
          lines,
        }), entryId(sType, adjRef.id));
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the adjustment.');
    }
    setSaving(false);
  }

  const selectedItem = items.find(i => i.id === itemId);

  return (
    <Modal open onClose={onClose} hideHead
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>Save Adjustment</Button>
      </>}>
      <ModalBanner title={selectedItem?.code || 'Adjust Stock'} eyebrow="Adjust Stock" onClose={onClose} />

      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

      <FormField label="Item" required>
        {/* Type a Code, shape or grade; Up / Down + Enter to pick (same picker as Company). */}
        <EntityCombobox
          entities={items.map(i => ({
            id: i.id,
            companyName: `${i.code ? `${i.code} · ` : ''}${i.shape} ${availCarat(i).toFixed(2)}ct ${i.color} ${i.clarity} · ${money(i.costUSD ?? 0)}`,
          }))}
          value={itemId} onChange={setItemId}
          placeholder="Type a Code, shape or grade…"
        />
      </FormField>

      <div className="form-row-2">
        <FormField label="Type" required>
          <select value={kind} onChange={e => setKind(e.target.value as StockAdjustKind)}>
            {(['loss', 'gain', 'writeoff'] as StockAdjustKind[]).map(k => (
              <option key={k} value={k}>{KIND_LABEL[k]}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', margin: '-4px 0 10px' }}>{KIND_BLURB[kind]}</div>

      {!isWriteoff && (
        <div className="form-row-2">
          <FormField label="Carat change" hint={item ? `${availCarat(item).toFixed(2)} ct available` : undefined}>
            <input type="number" step="0.01" min="0" className="no-spinner" value={caratMag}
              onChange={e => setCaratMag(e.target.value)} placeholder="0.00" />
          </FormField>
          <FormField label={`Value change (${settings.currency})`} hint={item ? `${money(item.costUSD ?? 0)} carrying cost` : undefined}>
            <input type="number" step="0.01" min="0" className="no-spinner" value={costMag}
              onChange={e => setCostMag(e.target.value)} placeholder="0.00" />
          </FormField>
        </div>
      )}
      {isWriteoff && item && (
        <div style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', fontSize: 13, marginBottom: 10 }}>
          Removes <strong>{availCarat(item).toFixed(2)} ct</strong> and books out <strong>{money(item.costUSD ?? 0)}</strong> of stock value.
        </div>
      )}

      <FormField label="Reason" required>
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. recount short, chipped in setting, stolen" />
      </FormField>
      <FormField label="Notes (optional)">
        <input value={notes} onChange={e => setNotes(e.target.value)} />
      </FormField>
    </Modal>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export default function StockAdjustmentPage() {
  const { business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [items, setItems] = useState<InventoryItem[]>([]);
  const [adjustments, setAdjustments] = useState<StockAdjustment[]>([]);
  const [loading, setLoading] = useState(true);

  const [dateKey, setDateKey] = useState<DateFilterKey>('this-month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [kindFilter, setKindFilter] = useState<'all' | StockAdjustKind>('all');
  const [searchQ, setSearchQ] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  // Opened from the Inventory popup (?item=…): start with that stone picked.
  const [preItem, setPreItem] = useState<string | undefined>();
  useItemParam(id => { setPreItem(id); setShowAdd(true); });
  const [confirm, setConfirm] = useState<StockAdjustment | null>(null);
  const [busy, setBusy] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(collection(db, 'inventory'),
      snap => setItems(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem)).filter(i => i.status === 'in-stock')),
      () => {});
    const u2 = onSnapshot(collection(db, 'stockAdjustments'),
      snap => { setAdjustments(snap.docs.map(d => ({ id: d.id, ...d.data() } as StockAdjustment))); setLoading(false); },
      () => setLoading(false));
    return () => { u1(); u2(); };
  }, [business]);

  const window = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);
  const rows = useMemo(() => {
    if (!window) return [];
    return adjustments
      .filter(a => inWindow(a.date, window))
      .filter(a => kindFilter === 'all' || a.kind === kindFilter)
      .filter(a => {
        const q = searchQ.trim().toLowerCase();
        return !q || [a.adjustmentNo, a.code, a.reason].some(v => (v ?? '').toLowerCase().includes(q));
      })
      .sort((a, b) => b.date.localeCompare(a.date) || toMillis(b.createdAt) - toMillis(a.createdAt));
  }, [adjustments, window, kindFilter, searchQ]);

  useEffect(() => { setCurrentPage(1); }, [dateKey, customFrom, customTo, kindFilter, searchQ]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const kpi = useMemo(() => {
    const inW = window ? adjustments.filter(a => inWindow(a.date, window)) : [];
    const lossVal = inW.filter(a => a.kind !== 'gain').reduce((s, a) => s + Math.abs(a.costDelta ?? 0), 0);
    const gainVal = inW.filter(a => a.kind === 'gain').reduce((s, a) => s + Math.abs(a.costDelta ?? 0), 0);
    return { lossVal, gainVal, count: inW.length };
  }, [adjustments, window]);

  async function doDelete(a: StockAdjustment) {
    setBusy(true);
    const db = getClientDb();
    if (!db || !business) { setBusy(false); return; }
    try {
      const itemRef = doc(db, 'inventory', a.inventoryItemId);
      const sType = a.kind === 'writeoff' ? 'stock_writeoff' : 'stock_adjustment';
      await runTransaction(db, async tx => {
        const is = await tx.get(itemRef);
        if (is.exists()) {
          const cur = is.data() as InventoryItem;
          if (a.kind === 'writeoff' || cur.status === 'written-off') {
            tx.update(itemRef, stripUndef({
              status: 'in-stock', writtenOffDate: null, writtenOffReason: null,
              updatedAt: serverTimestamp(),
            }));
          } else {
            tx.update(itemRef, {
              carat: round2((cur.carat ?? 0) - (a.caratDelta ?? 0)),
              costUSD: round2((cur.costUSD ?? 0) - (a.costDelta ?? 0)),
              costingUSD: round2((cur.costingUSD ?? 0) - (a.costingDelta ?? 0)),
              updatedAt: serverTimestamp(),
            });
          }
        }
        tx.delete(doc(db, 'journalEntries', entryId(sType, a.id)));
        tx.delete(doc(db, 'stockAdjustments', a.id));
      });
    } finally { setBusy(false); setConfirm(null); }
  }

  return (
    <AppLayout
      title="Stock Adjustment"
      subtitle="Correct carat or value on a stone, or write one off entirely — each correction is booked against Profit."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Ref, code, reason…" label="Search adjustments" />
        <DateRangeButton
          value={dateKey} onChange={setDateKey}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
        <ChoiceButton<'all' | StockAdjustKind>
          label="Type" value={kindFilter} onChange={setKindFilter} defaultKey="all"
          options={[
            { key: 'all', label: 'All' },
            { key: 'loss', label: 'Loss', dot: 'var(--danger)' },
            { key: 'gain', label: 'Gain', dot: 'var(--success)' },
            { key: 'writeoff', label: 'Write off', dot: 'var(--text-3)' },
          ]}
        />
        {(searchQ || dateKey !== 'this-month' || kindFilter !== 'all') && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateKey('this-month'); setKindFilter('all'); }}>Clear</button>
        )}
      </>}
      headerActions={
        <Button variant="primary" size="sm" icon={<IconPlusSm />} onClick={() => setShowAdd(true)}
          style={{ height: 34, boxSizing: 'border-box' }}>Adjust Stock</Button>
      }
    >
      <StatChips items={[
        { label: 'Written down', value: money(kpi.lossVal), tone: kpi.lossVal > 0 ? 'danger' : undefined },
        { label: 'Found', value: money(kpi.gainVal), tone: kpi.gainVal > 0 ? 'success' : undefined },
        { label: 'Adjustments', value: String(kpi.count) },
      ]} />

      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 200, borderRadius: 8 }} /></div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No adjustments here</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Use “Adjust Stock” above to record a correction.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 120 }}>Ref</th>
                  <th style={{ width: 110 }}>Type</th>
                  <th>Item</th>
                  <th>Reason</th>
                  <th style={{ textAlign: 'right', width: 90 }}>Carat</th>
                  <th style={{ textAlign: 'right', width: 120 }}>Value</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(a => (
                  <tr key={a.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{fmtDate(a.date)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{a.adjustmentNo}</td>
                    <td>
                      <span className="badge" style={{
                        fontSize: 10, fontWeight: 700,
                        background: a.kind === 'gain' ? 'var(--success-bg)' : 'var(--danger-bg)',
                        color: a.kind === 'gain' ? 'var(--success)' : 'var(--danger)',
                      }}>{a.kind === 'writeoff' ? 'Write-off' : a.kind === 'gain' ? 'Gain' : 'Loss'}</span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {a.code ? <span style={{ fontFamily: 'var(--font-num)' }}>{a.code}</span> : null}{a.code ? ' · ' : ''}{a.shape}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{a.reason}</td>
                    <td style={{ fontSize: 12, textAlign: 'right', fontFamily: 'var(--font-num)', color: (a.caratDelta ?? 0) < 0 ? 'var(--danger)' : 'var(--success)' }}>
                      {(a.caratDelta ?? 0) > 0 ? '+' : ''}{(a.caratDelta ?? 0).toFixed(2)}
                    </td>
                    <td style={{ fontSize: 12, textAlign: 'right', fontFamily: 'var(--font-num)', color: (a.costDelta ?? 0) < 0 ? 'var(--danger)' : 'var(--success)' }}>
                      {(a.costDelta ?? 0) > 0 ? '+' : ''}{money(a.costDelta ?? 0)}
                    </td>
                    <td className="col-actions">
                      <DeleteIconButton onClick={() => setConfirm(a)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <Pagination
          current={currentPage} total={totalPages}
          totalItems={rows.length} pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      )}

      {showAdd && <AdjustModal items={items} initialItemId={preItem} onClose={() => { setShowAdd(false); setPreItem(undefined); }} />}
      {confirm && (
        <ConfirmModal
          open
          title="Delete this adjustment?"
          message={`${confirm.adjustmentNo} will be removed and its effect on the item and Profit reversed.`}
          confirmLabel="Delete"
          loading={busy}
          onConfirm={() => doDelete(confirm)}
          onClose={() => setConfirm(null)}
        />
      )}
    </AppLayout>
  );
}
